import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import SortableList from '../common/SortableList';
import { normalizeId } from '../../utils/ids';
import { pinTargetOf } from '../../services/favorites';
import { RowMenu, pinMenu } from './HomeSections';
import RecordTile from './RecordTile';
import RecordTileModal from './RecordTileModal';
import RecordList from './RecordList';
import RecordListModal from './RecordListModal';
import { useTileValueContext } from './recordTileValues';
import useOpenPin from './useOpenPin';
import {
	isLauncherPin,
	isNewestPin,
	isRecordListPin,
	isTilePin,
	launchPin,
	pinAccessibleName,
	pinBadge,
	pinDescription,
	pinKicker,
	pinLabel,
	pinPath,
	pinTitle,
} from './homePins';

// The narrowest a cell may be (its pill and its ⋯), and the grid's gap (px).
// A wide desktop column fits five or six across; a 375px phone (343px inside
// the 16px gutters) is held at two (pinColumns never answers fewer), its
// cells a little narrower and their ⋯ folded away until Reorder.
export const PIN_CELL_MIN = 180;
export const PIN_CELL_GAP = 8;
// How many rows of cells the strip shows before "Show all (n)".
export const PIN_ROWS_SHOWN = 3;
// How many cells across a record tile (KPR-1127) and a list of records
// (KPR-1128) take.
export const TILE_SPAN = 2;

/** Is a pin drawn two cells wide (a record tile, a list of records)? */
export const isWidePin = pin => isTilePin(pin) || isRecordListPin(pin);

/** The cells a pin takes on the strip: a tile or a list two, any other one. */
export const pinSlots = pin => (isWidePin(pin) ? TILE_SPAN : 1);

/**
 * How many of `items` (in order) fit in `budget` cells — a tile counts its
 * two. At least one, so a strip never shows nothing.
 */
export function itemsWithin(items, budget) {
	let used = 0;
	let count = 0;
	for (const item of items) {
		const slots = pinSlots(item.pin);
		if (count > 0 && used + slots > budget) break;
		used += slots;
		count += 1;
	}
	return count;
}

/**
 * How many cells fit across `width` px: as many PIN_CELL_MIN cells (and
 * their gaps) as fit, never fewer than two. Before the strip has been
 * measured (width 0) it is two — the phone's count, the smallest promise.
 */
export function pinColumns(width) {
	if (!(width > 0)) return 2;
	return Math.max(2, Math.floor((width + PIN_CELL_GAP) / (PIN_CELL_MIN + PIN_CELL_GAP)));
}

/** The element's width, measured before paint and again whenever it changes. */
function useMeasuredWidth(ref) {
	const [width, setWidth] = useState(0);
	useLayoutEffect(() => {
		const node = ref.current;
		if (!node) return undefined;
		const measure = () => setWidth(node.clientWidth || 0);
		measure();
		if (typeof ResizeObserver === 'undefined') return undefined;
		const observer = new ResizeObserver(measure);
		observer.observe(node);
		return () => observer.disconnect();
	}, [ref]);
	return width;
}

/**
 * One pin: ONE compact cell — the record badge (one bordered pill: the icon
 * tile on the record's color, its kind over its name) as the link or launch
 * button, filling the cell; the grip before it in reorder mode, and a ⋯
 * after it. The cell names no
 * collection segment (there is no room for one); its tooltip and its
 * accessible name say where it lives.
 */
function PinCell({ pin, handle, ids, reordering, onReorder, onUnpin, onEditTile, onEditList, navigate, open, tileValues }) {
	const label = pinLabel(pin);
	const menu = (
		<RowMenu
			menu={pinMenu({ pin, ids, reordering, onReorder, onUnpin, onEditTile, onEditList, navigate })}
			name={label}
			className="home-cell__menu"
		/>
	);
	// A record tile (KPR-1127) is a cell of its own shape: two columns wide,
	// its values under its record.
	if (isTilePin(pin)) {
		return (
			<RecordTile
				pin={pin}
				defs={pin.item ? tileValues.defsFor(pin.item.card_id) : []}
				context={tileValues.context}
				handle={handle}
				menu={menu}
			/>
		);
	}
	// A list of records (KPR-1128): two columns wide, its records under its card.
	if (isRecordListPin(pin)) return <RecordList pin={pin} handle={handle} menu={menu} />;
	const badge = pinBadge(pin);
	// A newest-record pin (KPR-1126) asks which record when it is clicked, so
	// its pill is a button, like a launcher's.
	let go = { to: pinPath(pin) || undefined };
	if (isLauncherPin(pin)) go = { onClick: () => launchPin(navigate, pin) };
	else if (isNewestPin(pin)) go = { onClick: () => open(pin) };
	return (
		<div className="home-cell" data-pin-kind={pin.targetType}>
			{handle}
			<RecordBadge
				size="sm"
				block
				kind={badge.kind}
				record={badge.records ? undefined : badge.record}
				records={badge.records}
				fallbackIcon={badge.fallbackIcon}
				kicker={pinKicker(pin) || undefined}
				title={pinTitle(pin)}
				tooltip={pinDescription(pin)}
				aria-label={pinAccessibleName(pin)}
				className="home-cell__badge"
				{...go}
			/>
			{menu}
		</div>
	);
}

/**
 * Home's ONE Pinned strip (KPR-1123, KPE-036): every launcher, record, tag,
 * combined list, saved filter and record tile the person pinned, in the one order they set, as a grid of
 * compact cells (a record tile two cells wide, KPR-1127) — as many across as fit (two on a phone), the first three
 * rows shown and "Show all (n)" for the rest, so Recent and Collections stay
 * in reach. Reorder (the section's header) shows every cell with its grip and
 * Move up / Move down in its ⋯; `onReorder(ids)` gets the whole strip's new
 * order, by pin row id.
 */
export default function PinnedStrip({ pins, reordering = false, onReorder, onUnpin }) {
	const navigate = useNavigate();
	const open = useOpenPin();
	const ref = useRef(null);
	const columns = pinColumns(useMeasuredWidth(ref));
	const [expanded, setExpanded] = useState(false);
	const items = useMemo(() => (pins || []).map(pin => ({ id: normalizeId(pin._id), pin })), [pins]);
	const ids = useMemo(() => items.map(item => item.id), [items]);
	const tileValues = useTileValueContext(pins);
	// The tile whose values are being chosen (Edit tile…).
	const [editing, setEditing] = useState(null);
	// The list whose count is being chosen (Change how many…).
	const [listing, setListing] = useState(null);
	// Three rows of cells — a record tile takes two of a row's cells.
	const limit = columns * PIN_ROWS_SHOWN;
	const fits = itemsWithin(items, limit);
	const collapsible = fits < items.length;
	// Reorder mode shows every cell: a pin out of sight could not be moved.
	const shown = reordering || expanded || !collapsible ? items : items.slice(0, fits);
	return (
		<div ref={ref} className="home-strip" style={{ '--home-strip-cols': columns }}>
			<SortableList
				items={shown}
				layout="grid"
				ariaLabel="Pinned"
				className="home-strip__grid"
				itemClassName={item => (isWidePin(item.pin) ? 'home-strip__wide' : '')}
				getLabel={item => pinLabel(item.pin)}
				onReorder={onReorder}
				reordering={reordering}
				renderItem={({ pin }, { handle }) => (
					<PinCell
						pin={pin}
						handle={handle}
						ids={ids}
						reordering={reordering}
						onReorder={onReorder}
						onUnpin={onUnpin}
						onEditTile={setEditing}
						onEditList={setListing}
						navigate={navigate}
						open={open}
						tileValues={tileValues}
					/>
				)}
			/>
			{collapsible && !reordering ? (
				<Button
					type="link"
					className="home-strip__more"
					aria-expanded={expanded}
					onClick={() => setExpanded(open => !open)}
				>
					{expanded ? 'Show fewer' : `Show all (${items.length})`}
				</Button>
			) : null}
			{editing ? (
				<RecordTileModal
					open
					onClose={() => setEditing(null)}
					target={pinTargetOf(editing)}
					cardId={editing.source === 'latest' ? (editing.card_id || editing.card?._id) : (editing.item?.card_id || editing.card_id)}
					name={pinLabel(editing)}
				/>
			) : null}
			{listing ? (
				<RecordListModal
					open
					onClose={() => setListing(null)}
					target={pinTargetOf(listing)}
					name={pinLabel(listing)}
				/>
			) : null}
		</div>
	);
}
